<template>
  <component
    :is="tag"
    :type="tag === 'button' ? type : undefined"
    :disabled="disabled || loading"
    :class="classes"
    v-bind="$attrs"
  >
    <template v-if="spinIcon">
      <span
        class="inline-flex flex-shrink-0 items-center justify-center"
        :class="{ 'animate-spin-reverse': loading }"
      >
        <slot name="icon" />
      </span>
      <slot />
    </template>
    <template v-else-if="loading">
      <span
        class="inline-flex h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
      />
      <slot />
    </template>
    <template v-else>
      <span v-if="$slots.icon" class="inline-flex flex-shrink-0 items-center justify-center">
        <slot name="icon" />
      </span>
      <slot />
    </template>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline'
type Size = 'xs' | 'sm' | 'md' | 'lg'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    size?: Size
    tag?: string
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    loading?: boolean
    spinIcon?: boolean
  }>(),
  {
    variant: 'primary',
    size: 'md',
    tag: 'button',
    type: 'button',
    disabled: false,
    loading: false,
    spinIcon: false,
  },
)

const sizeClasses: Record<Size, string> = {
  xs: 'gap-1 rounded-md px-2 py-1 text-xs',
  sm: 'gap-1.5 rounded-lg px-3 py-1.5 text-sm',
  md: 'gap-2 rounded-lg px-4 py-2 text-sm',
  lg: 'gap-2 rounded-xl px-6 py-3 text-base',
}

const variantClasses: Record<Variant, string> = {
  primary: 'btn-brand-primary',
  secondary:
    'bg-gray-100 text-gray-800 hover:bg-gray-200 focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus-visible:ring-offset-gray-950',
  danger:
    'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:bg-red-300 dark:focus-visible:ring-offset-gray-950',
  ghost:
    'text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white dark:focus-visible:ring-offset-gray-950',
  outline:
    'border border-gray-300 text-gray-700 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:focus-visible:ring-offset-gray-950',
}

const classes = computed(() => {
  const base = [
    'inline-flex items-center justify-center font-medium transition-colors duration-150',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50',
    sizeClasses[props.size],
  ]
  if (props.variant === 'primary') {
    base.push(variantClasses.primary)
  } else {
    base.push(variantClasses[props.variant])
  }
  return base.join(' ')
})
</script>
